import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getUsers } from "../../utils/getUser";
import { base64ToFile, fileToBase64 } from "../../utils/util";
import { getUserByid, updateProfilePicture, updateUserProfile } from "../../reducers/authentication.slice";
import AddressAutocomplete from "../../components/AddressAutocompleteComponent/GoogleAddressInput";
import stateandcities from "../../assests/stateAndCities/stateandcities";
import ScrollToSection from "../../components/ScrollToSectionComponent";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectOption } from "../../components/ui/select";
import PageHeader from "../../components/shared/PageHeader";
import { AuthContext } from "../../context/authContext";

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

const EditProfilePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const myRef = useRef(null);

  const [showLoader, setShowLoader] = useState(false);
  const [profilePic, setProfilePic] = useState("");
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    id: "",
    role: "",
    discord_role: "",
    first_name: "",
    last_name: "",
    discord_id: "",
    email: "",
    status: "",
    address_details: { address: "", building: "", city: "", state: "", zipcode: "" },
    payment_details: {
      payment_method: "",
      account_holder_name: "",
      email: "",
      paypal_username: "",
      phone_number: "",
      routing_number: "",
      account_number: "",
    },
  });

  const { showToast } = useContext(AuthContext);

  useEffect(() => {
    setShowLoader(true);
    setUser(getUsers());
    if (!location.state?.isPaymentVia) {
      const newUserData = {
        ...getUsers(),
        discord_id: location.state?.user_detail?.discord_id,
        discord_role: location.state?.user_detail?.discord_role,
        permission_details: location.state?.user_detail?.permission_details,
        address_details: location.state?.user_detail?.address_details,
        payment_details: location.state?.user_detail?.payment_details,
        first_name: location.state?.user_detail?.first_name,
        last_name: location.state?.user_detail?.last_name,
        profile_picture: location.state?.user_detail?.profile_picture,
        role: location.state?.user_detail?.role,
        ip_address: location.state?.user_detail?.ip_address,
        last_login: location.state?.user_detail?.last_login,
        date_of_joining: location.state?.user_detail?.date_of_joining,
        login_type: location.state?.user_detail?.login_type,
      };
      setUser(newUserData);
      localStorage.setItem("user", JSON.stringify(newUserData));
      if (newUserData) {
        setFormData({
          id: newUserData?.id || "",
          first_name: newUserData?.first_name || "",
          last_name: newUserData?.last_name || "",
          role: newUserData?.role || "",
          discord_id: newUserData?.discord_id || "",
          discord_role: newUserData?.discord_role?.[0] || "",
          email: newUserData?.email || "",
          address_details: {
            address: newUserData?.address_details?.address || "",
            building: newUserData?.address_details?.building || "",
            city: newUserData?.address_details?.city || "",
            state: newUserData?.address_details?.state || "",
            zipcode: newUserData?.address_details?.zipcode || "",
          },
          payment_details: {
            payment_method: newUserData?.payment_details?.payment_method || "",
            account_holder_name: newUserData?.payment_details?.account_holder_name || "",
            email: newUserData?.payment_details?.email || "",
            paypal_username: newUserData?.payment_details?.paypal_username || "",
            phone_number: newUserData?.payment_details?.phone_number || "",
            routing_number: newUserData?.payment_details?.routing_number || "",
            account_number: newUserData?.payment_details?.account_number || "",
          },
          status: newUserData?.status || "",
        });
        setProfilePic(newUserData?.profile_picture || "");
      }
      setShowLoader(false);
      return;
    }
    const userId = getUsers()?.id;
    dispatch(getUserByid({ userId })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const newUserData = {
        ...getUsers(),
        discord_id: res?.payload?.discord_id,
        discord_role: res?.payload?.discord_role,
        permission_details: res?.payload?.permission_details,
        address_details: res?.payload?.address_details,
        payment_details: res?.payload?.payment_details,
        first_name: res?.payload?.first_name,
        last_name: res?.payload?.last_name,
        profile_picture: res?.payload?.profile_picture,
        role: res?.payload?.role,
        ip_address: res?.payload?.ip_address,
        last_login: res?.payload?.last_login,
        date_of_joining: res?.payload?.date_of_joining,
        login_type: res?.payload?.login_type,
      };
      setUser(newUserData);
      localStorage.setItem("user", JSON.stringify(newUserData));
      if (newUserData) {
        setFormData({
          id: newUserData?.id || "",
          first_name: newUserData?.first_name || "",
          last_name: newUserData?.last_name || "",
          role: newUserData?.role || "",
          discord_id: newUserData?.discord_id || "",
          discord_role: newUserData?.discord_role?.[0] || "",
          email: newUserData?.email || "",
          address_details: {
            address: newUserData?.address_details?.address || "",
            building: newUserData?.address_details?.building || "",
            city: newUserData?.address_details?.city || "",
            state: newUserData?.address_details?.state || "",
            zipcode: newUserData?.address_details?.zipcode || "",
          },
          payment_details: {
            payment_method: newUserData?.payment_details?.payment_method || "",
            account_holder_name: newUserData?.payment_details?.account_holder_name || "",
            email: newUserData?.payment_details?.email || "",
            paypal_username: newUserData?.payment_details?.paypal_username || "",
            phone_number: newUserData?.payment_details?.phone_number || "",
            routing_number: newUserData?.payment_details?.routing_number || "",
            account_number: newUserData?.payment_details?.account_number || "",
          },
          status: newUserData?.status || "",
        });
        setProfilePic(newUserData?.profile_picture || "");
      }
      setShowLoader(false);
    });
  }, []);

  const executeScroll = () => myRef.current.scrollIntoView();

  useEffect(() => {
    const isScroll = location.state?.isScroll ? true : false;
    if (isScroll) executeScroll();
  }, [location.state?.isScroll, user]);

  const handleFormData = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressAutocompleteChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      address_details: {
        ...prev.address_details,
        address: value?.street,
        city: value?.city,
        state: value?.state,
        zipcode: value?.zip,
      },
    }));
  };

  const handleAddressChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      address_details: { ...prev.address_details, [name]: value },
    }));
  };

  const handleUpdateUserProfile = () => {
    const payload = { ...formData };
    delete payload.discord_role;
    delete payload.email;
    delete payload.role;
    delete payload.status;
    dispatch(updateUserProfile(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) { showToast(res?.error?.message, "error"); return; }
      navigate("/dashboard/my-profile");
    });
  };

  const handleUpdateProfilePicture = async (event) => {
    if (event.target.files[0]) {
      const payload = { user_id: user?.id, file: event.target.files[0] };
      dispatch(updateProfilePicture(payload)).then(async (res) => {
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          showToast(res?.error?.message, "error");
          event.target.value = null;
          return;
        }
        const base64Img = await fileToBase64(event.target.files?.[0]);
        setProfilePic(base64Img);
        const newUserData = { ...getUsers(), profile_picture: base64Img };
        localStorage.setItem("user", JSON.stringify(newUserData));
        event.target.value = null;
        showToast("Profile picture uploaded successfully", "success");
      });
    }
  };

  const isFormDataUnchanged = () => {
    const isProfileChanged =
      formData.first_name !== user?.first_name ||
      formData.last_name !== user?.last_name ||
      formData.email !== user?.email;
    const isAddressChanged =
      formData.address_details?.address !== user?.address_details?.address ||
      formData.address_details?.building !== user?.address_details?.building ||
      formData.address_details?.city !== user?.address_details?.city ||
      formData.address_details?.state !== user?.address_details?.state ||
      formData.address_details?.zipcode !== user?.address_details?.zipcode;
    return !isProfileChanged && !isAddressChanged;
  };

  const profileImgSrc = profilePic
    ? URL.createObjectURL(base64ToFile(profilePic, "file", "image/*"))
    : null;

  return (
    <div style={{ maxWidth: 560, margin: "30px auto", width: "100%", padding: "0 0 60px" }}>
      <div
        onClick={() => navigate(-1)}
        style={{ display: "flex", alignItems: "center", gap: 6, color: "#ffffff85", cursor: "pointer", marginBottom: 16 }}
      >
        <ArrowLeft size={18} />
        <span style={{ fontFamily: "inter", fontSize: 14 }}>Back</span>
      </div>

      <div style={{ marginBottom: 24 }}>
        <PageHeader
          title="Edit Profile"
          subtitle="Update your profile details below."
        />
      </div>

      {/* Profile pic section */}
      <section style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: 12 }}>Profile Pic</h3>
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            padding: 16,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {profileImgSrc ? (
            <img
              src={profileImgSrc}
              alt={user?.first_name}
              style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid #fff" }}
            />
          ) : (
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: stringToColor((user?.first_name || "") + (user?.last_name || "")),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                border: "2px solid #fff",
              }}
            >
              {`${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase()}
            </div>
          )}
          <>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="profile-pic-upload"
              type="file"
              onChange={handleUpdateProfilePicture}
            />
            <label htmlFor="profile-pic-upload">
              <Button variant="default" style={{ cursor: "pointer" }} asChild>
                <span>Change</span>
              </Button>
            </label>
          </>
        </div>
      </section>

      {/* Basic info section */}
      <section style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: 12 }}>Basic Information</h3>
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            padding: 16,
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div>
            <Label htmlFor="first_name">First Name <span style={{ color: "red" }}>*</span></Label>
            <Input id="first_name" type="text" name="first_name" value={formData.first_name} placeholder="First Name" onChange={handleFormData} />
          </div>
          <div>
            <Label htmlFor="last_name">Last Name <span style={{ color: "red" }}>*</span></Label>
            <Input id="last_name" type="text" name="last_name" value={formData.last_name} placeholder="Last Name" onChange={handleFormData} />
          </div>
          <div>
            <Label htmlFor="discord_id">Discord Username</Label>
            <Input id="discord_id" type="text" name="discord_id" value={formData.discord_id} placeholder="Discord username" onChange={handleFormData} disabled />
          </div>
          <div>
            <Label htmlFor="discord_role">Discord Role</Label>
            <Input id="discord_role" type="text" name="discord_role" value={formData.discord_role || "No Role"} placeholder="Discord Role" disabled />
          </div>
          <div>
            <Label htmlFor="email">Email Address <span style={{ color: "red" }}>*</span></Label>
            <Input id="email" type="email" name="email" value={formData.email} placeholder="Email" onChange={handleFormData} disabled />
          </div>

          <div ref={myRef}>
            <Label htmlFor="address">Address</Label>
            <AddressAutocomplete
              onChange={(value) => handleAddressAutocompleteChange(value)}
              defaultValue={user?.address_details}
            />
          </div>
          <div>
            <Label htmlFor="building">Apartment/Suite (Optional)</Label>
            <Input
              id="building"
              type="text"
              placeholder="Apartment/Suite"
              value={formData?.address_details?.building}
              onChange={(e) => handleAddressChange("building", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="state">State</Label>
            <Select
              id="state"
              value={formData?.address_details?.state}
              onChange={(e) => handleAddressChange("state", e.target.value)}
            >
              {stateandcities.map((state, index) => (
                <SelectOption key={index} value={state.value}>{state.name}</SelectOption>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              type="text"
              placeholder="City"
              value={formData?.address_details?.city}
              onChange={(e) => handleAddressChange("city", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="zipcode">Zip Code</Label>
            <Input
              id="zipcode"
              type="text"
              placeholder="Zip Code"
              value={formData?.address_details?.zipcode}
              onChange={(e) => handleAddressChange("zipcode", e.target.value)}
            />
          </div>
        </div>
      </section>

      <Button disabled={isFormDataUnchanged()} onClick={handleUpdateUserProfile} style={{ width: "100%" }}>
        Save
      </Button>
    </div>
  );
};

export default EditProfilePage;
